import { useCallback, useContext, useMemo } from "react";

import { FeatureFlagValue } from "@simplyk/common";
import { useRouter } from "next/router";

import { FrontendFormContext } from "../contexts/FrontendFormContext";
import { SessionContext } from "../contexts/SessionContext";
import { trpc } from "../helpers/trpc";

import { TipSuggestionOutput } from "@/types/trpc";

interface UpsertSessionProps {
  displayedFormAmount: number;
  tipSuggestions: [TipSuggestionOutput, TipSuggestionOutput, TipSuggestionOutput] | undefined;
  isSuggestedTipsAmount: boolean;
  tipExperimentValue: FeatureFlagValue | null;
}

export const useUpsertSession = ({
  displayedFormAmount,
  tipSuggestions,
  isSuggestedTipsAmount,
  tipExperimentValue,
}: UpsertSessionProps) => {
  const router = useRouter();
  const { mutateAsync: execUpsertSession } = trpc.form_upsertSession.useMutation();

  const { sessionId } = useContext(SessionContext);
  const { formData, formType } = useContext(FrontendFormContext);

  const userAgent = useMemo(() => {
    if (typeof window === "undefined") {
      return {
        appCodeName: "",
        appName: "",
        language: "",
        appVersion: "",
        userAgent: "",
      };
    }
    return {
      appCodeName: process.browser ? window.navigator.appCodeName : "",
      appName: process.browser ? window.navigator.appName : "",
      language: process.browser ? window.navigator.language : "",
      appVersion: process.browser ? window.navigator.appVersion : "",
      userAgent: window.navigator.userAgent,
    };
  }, []);
  const suggestedTips = useMemo(() => tipSuggestions?.map((tip) => tip.amount), [tipSuggestions]);

  const upsertSession = useCallback(async () => {
    await execUpsertSession({
      ...userAgent,
      url: router.asPath,
      isEmbed: router.asPath.includes("embed"),
      id: sessionId,
      formId: formData?.id,
      formType,
      amount: displayedFormAmount ? Math.round(displayedFormAmount) : undefined,
      suggestedTips: suggestedTips?.[0] ? suggestedTips : undefined,
      defaultSuggestedTip: suggestedTips?.[0] ? suggestedTips?.[0] : undefined,
      isSuggestedTipsAmount,
      tipExperimentValue,
    });
  }, [
    execUpsertSession,
    userAgent,
    router.asPath,
    sessionId,
    formData?.id,
    formType,
    displayedFormAmount,
    suggestedTips,
    isSuggestedTipsAmount,
    tipExperimentValue,
  ]);

  return { upsertSession };
};
